import type {
  CaptureOptionsBySource,
  ToggleSource,
} from "@livekit/components-core";
import * as React from "react";
import { getSourceIcon } from "./utils";
import { useTrackToggle } from "./hooks/useTrackToggle";
import { useRecoilValue } from "recoil";
import { staticConfigAtom } from "../../atom/atom";
import { StaticConfig } from "../../models/IStaticConfig";
import { Track } from "livekit-client";

/** @public */
export interface TrackToggleProps<T extends ToggleSource>
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  source: T;
  showIcon?: boolean;
  initialState?: boolean;
  /**
   * Function that is called when the enabled state of the toggle changes.
   * The second function argument `isUserInitiated` is `true` if the change was initiated by a user interaction, such as a click.
   */
  onChange?: (enabled: boolean, isUserInitiated: boolean) => void;
  captureOptions?: CaptureOptionsBySource<T>;
}

/**
 * With the `TrackToggle` component it is possible to mute and unmute your camera and microphone.
 * The component uses an html button element under the hood so you can treat it like a button.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <TrackToggle source={Track.Source.Microphone} />
 *   <TrackToggle source={Track.Source.Camera} />
 * </LiveKitRoom>
 * ```
 * @public
 */
export function TrackToggle<T extends ToggleSource>({
  showIcon,
  ...props
}: TrackToggleProps<T>) {

  const staticConfigInfo: StaticConfig | null = useRecoilValue(staticConfigAtom);
  const getDisabledCondition = (source: string) => {
    switch (source) {
      case Track.Source.Camera:
        return !(staticConfigInfo?.features?.video);
      case Track.Source.Microphone:
        return !(staticConfigInfo?.features?.audio);
      default:
        return false;
      
    }
  };
  const { buttonProps, enabled } = useTrackToggle(props);
  return (
    <button  {...buttonProps} disabled={getDisabledCondition(props.source)} >
      {(showIcon ?? true) && getSourceIcon(props.source, enabled)}
      {props.children}
    </button>
  );
}

